/* eslint-disable import/extensions */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { handleCookie } from '@utils';

import { AppState } from '@modules';

const { getCookie } = handleCookie;

const createInstance = (baseURL: string) =>
  axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Accept: 'application/json',
    },
    withCredentials: true,
    validateStatus: status => [200, 201].includes(status),
  });

export const apiInstance = createInstance(CONST.api.url);
apiInstance.interceptors.response.use(res => res.data);

export const apiGoInstance = createInstance(CONST.apiGo.url);
apiGoInstance.interceptors.response.use(res => res);

const getDefaultHeaders = (
  axiosHeaders: AxiosRequestConfig['headers'],
  state: AppState
): typeof axiosHeaders => {
  const headers = axiosHeaders || {};
  const { login, global } = state;
  const { token, verification } = login;
  const { calcId, lang } = global;
  const language_code = lang && CONST.i18n.languages[lang]?.code;
  const { editToken } = verification;
  const apiToken = IS_DEV && (token || getCookie('token'));
  if (editToken) headers['X-Api-Edit-Token'] = editToken;
  if (apiToken) headers['X-Api-Token'] = apiToken;
  if (calcId) headers['X-Calc-Id'] = calcId;
  if (language_code) headers['Accept-Language'] = language_code;
  return headers;
};

const get =
  (instance: AxiosInstance) =>
  <T>(
    thunkApi: Parameters<Parameters<typeof createAsyncThunk>[1]>[1],
    url: Parameters<typeof apiInstance.get>[0],
    params?: Parameters<typeof apiInstance.get>[1]
  ) => {
    const state = thunkApi.getState() as AppState;
    const headers = getDefaultHeaders(params?.headers, state);
    return instance.get<T>(url, { ...params, headers });
  };

const post =
  (instance: AxiosInstance) =>
  <T>(
    thunkApi: Parameters<Parameters<typeof createAsyncThunk>[1]>[1],
    url: Parameters<typeof apiInstance.post>[0],
    data?: Parameters<typeof apiInstance.post>[1],
    config?: Parameters<typeof apiInstance.post>[2]
  ) => {
    const state = thunkApi.getState() as AppState;
    const headers = getDefaultHeaders(config?.headers, state);
    return instance.post<T>(url, data, { ...config, headers });
  };

try {
  /* eslint-disable-next-line global-require */
  const mocks = require('./mocks.ts');
  mocks.init(apiInstance, apiGoInstance);
} catch (err) {
  // eslint-disable-next-line no-console
  if (IS_DEV) console.error('PROBLEM WITH MOCK FILE');
}

const api = {
  get: get(apiInstance),
  post: post(apiInstance),
  getGo: get(apiGoInstance),
  postGo: post(apiGoInstance),
};

export default api;
